import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { computePL } from '../utils';
import PLDashboard from '../components/PLDashboard';
import DealSetupSection from '../components/DealSetupSection';
import CostSection from '../components/CostSection';
import LedgerSection from '../components/LedgerSection';
import CapitalPartnerSection from '../components/CapitalPartnerSection';
import SOWSection from '../components/SOWSection';
import MaterialsSection from '../components/MaterialsSection';
import PhotosSection from '../components/PhotosSection';
import ChatPanel from '../components/ChatPanel';

const TABS = ['Setup', 'SOW', 'Costs', 'Materials', 'Photos', 'Ledgers'];

const STATUS_CLASS = {
  'Pre-Deal': 'status-pre-deal', 'Purchased': 'status-purchased',
  'On Market': 'status-on-market', 'Under Contract': 'status-under-contract', 'Sold': 'status-sold',
};

export default function DealPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [tab, setTab] = useState('Setup');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.getDeal(id).then(d => { setDeal(d); setLoading(false); });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!window.confirm('Delete this deal? This cannot be undone.')) return;
    await api.deleteDeal(id);
    navigate('/');
  };

  if (loading) return (
    <>
      <AppHeader />
      <div className="page" style={{textAlign:'center',paddingTop:60,color:'var(--text-muted)'}}>Loading deal…</div>
    </>
  );

  if (!deal) return (
    <>
      <AppHeader />
      <div className="page"><div className="empty-state"><div>Deal not found.</div></div></div>
    </>
  );

  const pl = computePL(deal);

  return (
    <>
      <AppHeader />
      <div className="page">
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate('/')}>←</button>
          <div>
            <h1>{deal.address}</h1>
            <span className={`badge ${STATUS_CLASS[deal.status] || ''}`} style={{marginTop:4}}>{deal.status}</span>
          </div>
          <div style={{marginLeft:'auto',display:'flex',gap:8}}>
            <button
              className="btn-secondary"
              style={{fontSize:12}}
              onClick={() => window.open(`/deals/${id}/report?mode=sow`, '_blank')}
              title="SOW + materials only — no financials (safe to share with contractors)"
            >
              📋 SOW Export
            </button>
            <button
              className="btn-secondary"
              style={{fontSize:12}}
              onClick={() => window.open(`/deals/${id}/report?mode=full`, '_blank')}
              title="Full report with P&L, ARV, ROI, cash to close"
            >
              📊 Full Report
            </button>
            <button className="btn-danger" style={{fontSize:12}} onClick={handleDelete}>
              Delete Deal
            </button>
          </div>
        </div>

        <div className="tab-bar">
          {TABS.map(t => (
            <button key={t} className={`tab-btn${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>

        <div className="deal-page-layout">
          <div>
            {tab === 'Setup' && (
              <>
                <DealSetupSection deal={deal} onUpdate={load} />
                <CapitalPartnerSection deal={deal} onUpdate={load} />
              </>
            )}

            {tab === 'SOW' && (
              <SOWSection deal={deal} onUpdate={load} />
            )}

            {tab === 'Costs' && (
              <>
                <CostSection
                  deal={deal} table="frontend_costs" title="Front-End Costs"
                  benchmarkLabel={`${deal.frontend_pct || 4}% of ARV benchmark`}
                  benchmarkValue={pl.feBenchmark}
                  onUpdate={load}
                />
                <CostSection
                  deal={deal} table="holding_costs" title="Holding Costs"
                  benchmarkLabel={`${deal.holding_pct_per_month || 1}%/mo × ${deal.hold_months || 6} months benchmark`}
                  benchmarkValue={pl.hcBenchmark}
                  onUpdate={load}
                />
                <CostSection
                  deal={deal} table="backend_costs" title="Back-End Costs"
                  benchmarkLabel={`${deal.backend_pct || 8}% of ARV benchmark`}
                  benchmarkValue={pl.beBenchmark}
                  onUpdate={load}
                />
              </>
            )}

            {tab === 'Materials' && (
              <MaterialsSection deal={deal} />
            )}

            {tab === 'Photos' && (
              <PhotosSection deal={deal} />
            )}

            {tab === 'Ledgers' && (
              <>
                <LedgerSection
                  deal={deal} ledgerType="bryce" title="Bryce's Out-of-Pocket Expenses"
                  entries={deal.expenses_bryce || []} onUpdate={load}
                />
                {!!deal.has_partner && (
                  <LedgerSection
                    deal={deal} ledgerType="partner" title={`${deal.cp_name || 'Partner'}'s Expenses`}
                    entries={deal.expenses_partner || []} onUpdate={load}
                  />
                )}
                <LedgerSection
                  deal={deal} ledgerType="closing" title="Closing Statement Line Items"
                  entries={deal.expenses_closing || []} onUpdate={load}
                />
              </>
            )}
          </div>

          {/* P&L sidebar — always visible */}
          <PLDashboard deal={deal} />
        </div>
      </div>

      {/* Mo — floating chat assistant */}
      <ChatPanel deal={deal} onUpdate={load} />
    </>
  );
}

function AppHeader() {
  const navigate = useNavigate();
  return (
    <header className="app-header" style={{cursor:'pointer'}} onClick={() => navigate('/')}>
      <img src="/logo-white.png" alt="My Fair Market Offer" style={{height:36,width:'auto'}} />
      <div className="logo-divider" />
      <span style={{color:'rgba(255,255,255,0.75)',fontSize:13,fontWeight:500,letterSpacing:'0.03em'}}>
        Rehab Estimator
      </span>
    </header>
  );
}
